import React, { useState } from 'react';
import type { DonationAmount } from '@/types';
import { Button } from '@components/common';
import tier5Image from '@assets/images/donations/tier-5.png';
import tier10Image from '@assets/images/donations/tier-10.png';
import tier25Image from '@assets/images/donations/tier-25.png';
import tier50Image from '@assets/images/donations/tier-50.png';
import './AmountPicker.css';

const DONATION_AMOUNTS: DonationAmount[] = [
  { amount: 5, image: tier5Image },
  { amount: 10, image: tier10Image },
  { amount: 25, image: tier25Image },
  { amount: 50, image: tier50Image },
];

interface AmountPickerProps {
  onSelect: (amount: number) => void;
}

interface MinAmountPopupProps {
  onClose: () => void;
  onProceed: () => void;
}

const MinAmountPopup: React.FC<MinAmountPopupProps> = ({ onClose, onProceed }) => (
  <div className="min-amount-popup-overlay">
    <div className="min-amount-popup">
      <div className="min-amount-popup__icon">🔑</div>
      <h2 className="min-amount-popup__title">Archive Access Info</h2>
      <p className="min-amount-popup__message">
        The minimum donation to receive the <strong>archive password</strong> is <strong>10€</strong>.
      </p>
      <div className="min-amount-popup__benefits">
        <p className="min-amount-popup__benefits-title">With the password you get access to:</p>
        <ul className="min-amount-popup__list">
          <li>1,300,000+ games across all platforms</li>
          <li>200+ emulators for every console</li>
          <li>Long-term access (password won't change)</li>
          <li>Regular archive updates</li>
        </ul>
      </div>
      <div className="min-amount-popup__actions">
        <Button variant="secondary" onClick={onProceed}>
          Donate 5€ anyway
        </Button>
        <Button variant="primary" onClick={onClose}>
          Choose 10€+
        </Button>
      </div>
    </div>
  </div>
);

export const AmountPicker: React.FC<AmountPickerProps> = ({ onSelect }) => {
  const [showMinAmountPopup, setShowMinAmountPopup] = useState(false);

  const handleAmountClick = (amount: number) => {
    if (amount === 5) {
      setShowMinAmountPopup(true);
    } else {
      onSelect(amount);
    }
  };

  const handleProceedWith5 = () => {
    setShowMinAmountPopup(false);
    onSelect(5);
  };

  const handleClosePopup = () => {
    setShowMinAmountPopup(false);
  };

  return (
    <>
      {showMinAmountPopup && (
        <MinAmountPopup onClose={handleClosePopup} onProceed={handleProceedWith5} />
      )}
      <div className="amount-picker">
        {DONATION_AMOUNTS.map(({ amount, image }) => (
          <button
            key={amount}
            type="button"
            className="amount-picker__item"
            onClick={() => handleAmountClick(amount)}
            aria-label={`Donate ${amount} euros`}
          >
            <img
              src={image}
              alt={`${amount}€ donation tier`}
              className="amount-picker__image"
            />
            <span className="amount-picker__amount">{amount} €</span>
          </button>
        ))}
      </div>
    </>
  );
};

export default AmountPicker;
